import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Edit2, Eye, EyeOff, Save, Loader2, X, ChevronRight, FolderOpen } from 'lucide-react';
import api from '../api';
import { SECTION_SCHEMAS, SECTION_LABELS, SECTION_GROUPS } from '../sectionSchemas';
import RepeaterField from '../RepeaterField';
import MediaPickerModal from '../components/MediaPickerModal';

/* ─── Card in the section list ──────────────────────────────────────────── */

function SectionCard({ section, onEdit, onToggleVisible }) {
  const [toggling, setToggling] = useState(false);
  const handleToggle = async () => {
    setToggling(true);
    await onToggleVisible(section);
    setToggling(false);
  };
  return (
    <div
      className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 flex items-center justify-between gap-4"
      data-testid={`section-card-${section.key}`}
    >
      <div className="min-w-0">
        <p className="font-semibold text-slate-800 text-sm">{SECTION_LABELS[section.key] || section.key}</p>
        <p className="text-xs text-slate-400 mt-0.5 font-mono">{section.key}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={handleToggle}
          disabled={toggling}
          data-testid={`toggle-section-${section.key}`}
          title={section.visible ? 'Hide section' : 'Show section'}
          className={`p-2 rounded-lg transition-colors ${
            section.visible
              ? 'text-emerald-600 bg-emerald-50 hover:bg-emerald-100'
              : 'text-slate-400 bg-slate-100 hover:bg-slate-200'
          }`}
        >
          {toggling ? <Loader2 className="w-4 h-4 animate-spin" /> : section.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
        </button>
        <button
          onClick={() => onEdit(section)}
          data-testid={`edit-section-${section.key}`}
          className="p-2 rounded-lg text-[#1F2D45] bg-slate-100 hover:bg-slate-200 transition-colors"
        >
          <Edit2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

/* ─── Image preview for url fields ─────────────────────────────────────── */

function ImagePreview({ src }) {
  const [errored, setErrored] = useState(false);
  if (!src || errored) return null;
  return (
    <img
      key={src}
      src={src}
      alt="Preview"
      className="mt-2 h-20 w-full rounded-lg object-cover border border-slate-200 bg-slate-50"
      onError={() => setErrored(true)}
    />
  );
}

/* ─── Form modal for editing a section ─────────────────────────────────── */

function SectionForm({ section, schema, onSave, onClose, saving }) {
  const [mediaPickerField, setMediaPickerField] = useState(null);
  const [form, setForm] = useState(() => {
    const base = {};
    schema.forEach((f) => {
      const raw = section.data?.[f.key];
      if (f.type === 'json') {
        if (f.subfields) {
          base[f.key] = Array.isArray(raw) ? raw : [];
        } else {
          base[f.key] = raw == null ? '' : (typeof raw === 'string' ? raw : JSON.stringify(raw, null, 2));
        }
      } else {
        base[f.key] = raw ?? '';
      }
    });
    return base;
  });
  const [jsonErrors, setJsonErrors] = useState({});

  const set = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const handleSave = () => {
    const errors = {};
    const out = {};

    schema.forEach((f) => {
      if (f.type !== 'json') {
        out[f.key] = form[f.key] || '';
        return;
      }

      if (f.subfields) {
        const items = Array.isArray(form[f.key]) ? form[f.key] : [];
        const requiredKeys = f.subfields.filter((sf) => sf.required).map((sf) => sf.key);
        if (requiredKeys.length > 0 && items.length > 0) {
          const badIndex = items.findIndex((item) =>
            requiredKeys.some((k) => item[k] === undefined || item[k] === null || item[k] === '')
          );
          if (badIndex !== -1) {
            const missingLabels = requiredKeys
              .filter((k) => item => item[k] === undefined || item[k] === null || item[k] === '')
              .map((k) => f.subfields.find((sf) => sf.key === k)?.label || k)
              .join(', ');
            errors[f.key] = `Item ${badIndex + 1} is missing: ${missingLabels}`;
          }
        }
        out[f.key] = items.length > 0 ? items : null;
      } else {
        const v = (form[f.key] ?? '').trim();
        if (!v) {
          out[f.key] = null;
          return;
        }
        try {
          out[f.key] = JSON.parse(v);
        } catch (e) {
          errors[f.key] = e.message;
        }
      }
    });

    if (Object.keys(errors).length) {
      setJsonErrors(errors);
      return;
    }
    setJsonErrors({});
    onSave(out);
  };

  const cls = 'w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9962E]/40';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="font-bold text-[#1F2D45]">Edit: {SECTION_LABELS[section.key] || section.key}</h2>
            <p className="text-xs text-slate-400 font-mono mt-0.5">{section.key}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {schema.map((field) => (
            <div key={field.key}>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                {field.label}
                {field.required && <span className="text-red-500 ml-0.5">*</span>}
              </label>

              {field.type === 'textarea' ? (
                <textarea
                  value={form[field.key] ?? ''}
                  onChange={(e) => set(field.key, e.target.value)}
                  rows={field.rows || 3}
                  data-testid={`field-${field.key}`}
                  className={`${cls} resize-y`}
                />
              ) : field.type === 'json' && field.subfields ? (
                <RepeaterField
                  value={form[field.key]}
                  onChange={(val) => set(field.key, val)}
                  subfields={field.subfields}
                  fieldLabel={field.label}
                />
              ) : field.type === 'json' ? (
                <textarea
                  value={form[field.key] ?? ''}
                  onChange={(e) => set(field.key, e.target.value)}
                  rows={6}
                  data-testid={`field-${field.key}`}
                  placeholder='e.g. [{"key": "value"}]'
                  className={`${cls} resize-y font-mono text-xs`}
                />
              ) : field.type === 'url' && field.mediaPicker ? (
                <div className="space-y-1.5">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={form[field.key] ?? ''}
                      onChange={(e) => set(field.key, e.target.value)}
                      data-testid={`field-${field.key}`}
                      placeholder="Paste URL or pick from library"
                      className={`flex-1 ${cls}`}
                    />
                    <button
                      type="button"
                      onClick={() => setMediaPickerField(field.key)}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-600 hover:bg-slate-50 whitespace-nowrap"
                    >
                      <FolderOpen className="w-4 h-4" /> Browse
                    </button>
                  </div>
                  <ImagePreview src={form[field.key]} />
                </div>
              ) : field.type === 'number' ? (
                <input
                  type="number"
                  value={form[field.key] ?? ''}
                  onChange={(e) => set(field.key, e.target.value === '' ? '' : Number(e.target.value))}
                  data-testid={`field-${field.key}`}
                  className={cls}
                />
              ) : (
                <input
                  type="text"
                  value={form[field.key] ?? ''}
                  onChange={(e) => set(field.key, e.target.value)}
                  data-testid={`field-${field.key}`}
                  className={cls}
                />
              )}

              {field.type === 'url' && !field.mediaPicker && (
                <ImagePreview src={form[field.key]} />
              )}
              {jsonErrors[field.key] && (
                <p className="mt-1 text-[11px] text-red-600">{jsonErrors[field.key]}</p>
              )}
            </div>
          ))}
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-sm text-slate-600 hover:bg-slate-50">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-section-btn"
            className="flex items-center gap-2 px-5 py-2 rounded-lg bg-[#1F2D45] text-white text-sm font-semibold hover:bg-[#2a3d5e] disabled:opacity-60 transition-colors"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            <Save className="w-4 h-4" /> Save
          </button>
        </div>
      </div>
      {mediaPickerField && (
        <MediaPickerModal
          onSelect={(url) => { set(mediaPickerField, url); setMediaPickerField(null); }}
          onClose={() => setMediaPickerField(null)}
        />
      )}
    </div>
  );
}

/* ─── Main editor page ─────────────────────────────────────────────────── */

export default function SectionsEditor() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const { data: sections = [], isLoading } = useQuery({
    queryKey: ['admin-sections'],
    queryFn: () => api.get('/admin/sections').then((r) => r.data.data),
  });

  const handleEdit = (section) => setEditing(section);

  const handleSave = async (formData) => {
    setSaving(true);
    try {
      await api.put(`/admin/sections/${editing.key}`, { data: formData });
      qc.invalidateQueries({ queryKey: ['admin-sections'] });
      toast.success(`"${SECTION_LABELS[editing.key] || editing.key}" updated`);
      setEditing(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleVisible = async (section) => {
    try {
      await api.put(`/admin/sections/${section.key}`, { visible: !section.visible });
      qc.invalidateQueries({ queryKey: ['admin-sections'] });
      toast.success(`Section ${section.visible ? 'hidden' : 'shown'}`);
    } catch {
      toast.error('Could not update visibility');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  // Build a map for quick lookup
  const sectionMap = {};
  sections.forEach((s) => { sectionMap[s.key] = s; });

  // Build list of keys already assigned to a group
  const groupedKeys = new Set();
  SECTION_GROUPS.forEach((g) => g.keys.forEach((k) => groupedKeys.add(k)));

  // Find ungrouped sections
  const ungrouped = sections.filter((s) => !groupedKeys.has(s.key));

  return (
    <div className="space-y-6" data-testid="sections-editor">
      <div>
        <h1 className="text-2xl font-bold text-[#1F2D45]">Sections</h1>
        <p className="text-slate-500 text-sm mt-1">Edit content for each page section. Toggle visibility with the eye icon.</p>
      </div>

      {/* Grouped sections */}
      <div className="space-y-8">
        {SECTION_GROUPS.map((group) => {
          const groupSections = group.keys.map((k) => sectionMap[k]).filter(Boolean);
          if (groupSections.length === 0) return null;
          return (
            <div key={group.label}>
              <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">{group.label}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {groupSections.map((s) => (
                  <SectionCard key={s.key} section={s} onEdit={handleEdit} onToggleVisible={handleToggleVisible} />
                ))}
              </div>
            </div>
          );
        })}

        {ungrouped.length > 0 && (
          <div>
            <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Other</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {ungrouped.map((s) => (
                <SectionCard key={s.key} section={s} onEdit={handleEdit} onToggleVisible={handleToggleVisible} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Edit modal */}
      {editing && (
        <SectionForm
          section={editing}
          schema={SECTION_SCHEMAS[editing.key] || []}
          onSave={handleSave}
          onClose={() => setEditing(null)}
          saving={saving}
        />
      )}
    </div>
  );
}
